import React, { useContext, useEffect, useState } from "react";
import {
	Button,
	Dialog,
	DialogActions,
	DialogTitle,
	DialogContent,
	TextField,
	Stack,
	Radio,
	RadioGroup,
	FormControl,
	FormControlLabel,
	MenuItem,
	InputLabel,
	Select,
    Divider,
    LinearProgress,
    CircularProgress
} from "@mui/material";
import dayjs from "dayjs";
import CheckCircleSharpIcon from "@mui/icons-material/CheckCircleSharp";
import CancelSharpIcon from "@mui/icons-material/CancelSharp";

import "./billable-items-style.scss";

import config from "../../hoa-config";
import datastore from "../../store/hoa-datastore";
import CustomDateField from "../../Container/custom-date-field";
import { GlobalContext } from "../../context/hoa-global-context";
import { useSelectedTaskStoreStore } from "../../store/useTaskStore";
import dashboardServices from "../../services/hoa-dashboard-api-service";

export default function BillableItemsModal({
	selectedBillItem,
	setSelectedBillItem,
	evidenceLink,
	setEvidenceLink,
	onSaveClick,
	isBillableItemsModal,
	setIsBillableItemsModal,
	tagType,
	handleSaveModalOpen,
	sourceId,
	setSourceId,
	isVerified,
	setIsVerified,
	setSourceDocumentType,
	effectiveDate,
	setEffectiveDate,
	documentType,
	setDocumentType,
	isEditMode,
	billItems,
	isOtherBillableItemsDisabled,
	shouldShowClientChargeWarning,
	linkedEntryId,
	setLinkedEntryId,
	documentId,
	setDocumentId,
	isDocumentIdVerified,
	setIsDocumentIdVerified
}: any) {

	const { selectedValues, property, activeMenu } = useContext<any>(GlobalContext);
	const { findDataMatchWithSelectedValue } = useSelectedTaskStoreStore();

	const [sourceIdError, setSourceIdError] = useState(false);
	const [verificationLoading, setVerificationLoading] = useState(false);
	const [isLinkedEntryIdVerified, setIsLinkedEntryIdVerified] = useState(null);
	const [loadingLinkedEntryIdVerify, setLoadingLinkedEntryIdVerify] = useState(false);

	const [documentIdError, setDocumentIdError] = useState(false);

	useEffect(() => {
		setIsDocumentIdVerified(false)
	}, [documentId])

	useEffect(() => {
		setDocumentId("");
		setIsDocumentIdVerified(false);
}, [selectedBillItem]);


	useEffect(() => {
		if (tagType !== "Property") return;

		if (isBillableItemsModal === true && activeMenu === "propertiespendingtask") {
			const propertyValue = findDataMatchWithSelectedValue(property.data, datastore);
			if (propertyValue?.mailDetails?.id && propertyValue?.taskSourceType === "mail") {
				setSourceId(propertyValue?.mailDetails?.id || "");
				setSelectedBillItem("Update from mail");
				setSourceDocumentType("mail");
				setIsVerified({ attachmentDetails: propertyValue?.mailDetails?.attachmentDetails });
			} else if (
				propertyValue?.emailDetails?.id &&
				propertyValue?.taskSourceType === "email"
			) {
				setSourceId(propertyValue?.emailDetails?.id || "");
				setSelectedBillItem("Update from email");
				setSourceDocumentType("email");
				setIsVerified({
					attachmentDetails: propertyValue?.emailDetails?.attachmentDetails,
				});
			}
			return;
		}
		return () => {
			setIsVerified(null);
		};
	}, [isBillableItemsModal, selectedBillItem]);

	const isItemVerifiedFromPropertyTask = () => {
		return activeMenu === "propertiespendingtask";
	};

	useEffect(() => {
		setIsLinkedEntryIdVerified(false);
	}, [linkedEntryId]);

	const handleClose = () => {
		setSelectedBillItem(null);
		setEvidenceLink(null);
		setIsBillableItemsModal(false);
		setLinkedEntryId(null);
		if (tagType === "Hoa" && !isEditMode) {
			setEffectiveDate(null);
			setDocumentType("");
		}
	};

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (effectiveDate && event.target.value === "Not Billable") {
			alert("Not billable item cannot have effective date");
			return;
		}
		setSelectedBillItem((event.target as HTMLInputElement).value);
		setSourceId("");
		setIsVerified(null);
		setSourceIdError(false);
		setSourceDocumentType("");
	};

	const saveBtnClicked = () => {
		if (
			(tagType === "Hoa" || tagType === "Property") &&
			(selectedBillItem === "Update from mail" || selectedBillItem === "Update from email") &&
			!sourceId
		) {
			setSourceIdError(true);
			return;
		}
		if (sourceId && !isVerified) {
			setSourceIdError(true);
			return;
		}
		handleClose();
		if (
			tagType === "Hoa" &&
			typeof shouldShowClientChargeWarning === "function" &&
			shouldShowClientChargeWarning()
		) {
			handleSaveModalOpen();
		} else {
			onSaveClick();
		}
		setSourceIdError(false);
		setSourceId("");
		setIsVerified(null);
		setSourceDocumentType("");
		setIsLinkedEntryIdVerified(null);
	};

	const renderBillItems = () => {
		if (!billItems || billItems?.length == 0) {
			return <></>;
		}

		return (
			<>
				<FormControlLabel
					disabled={isBillableItemDisabled() || isItemVerifiedFromPropertyTask()}
					value={billItems[0]?.billItem}
					control={<Radio />}
					label={billItems[0]?.billItem}
				/>
				<Divider
					style={{ marginTop: 10, marginBottom: 10, backgroundColor: "#0000004d" }}
				/>
				{billItems?.slice(1).map((item: any, index: any) => {
					return (
						<FormControlLabel
							disabled={isOtherBillableItemsDisabled}
							key={index}
							value={item.billItem}
							control={<Radio />}
							label={item.billItem}
						/>
					);
				})}
			</>
		);
	};

	if (!billItems) {
		return <></>;
	}

	const handleVerifySourceId = async () => {
		if (!sourceId) {
			setSourceIdError(true);
		} else {
			setIsVerified(null);
			setVerificationLoading(true);
			const type = selectedBillItem === "Update from mail" ? "mail" : "email";
			const response = await dashboardServices.verifySourceId(sourceId, type);
			if (response && response.data) {
				setIsVerified(response.data);
				setSourceDocumentType(type);
			}
			setVerificationLoading(false);
		}
	};

	const handleSourceId = (value: string) => {
		setSourceId(value);
		setIsVerified(null);
	};

	const isBillableItemDisabled = () => {
		return datastore.userRole === "officer" && effectiveDate;
	};

	const onEffectiveDateChange = (value: any) => {
		if (selectedBillItem === "Not Billable") {
			alert("Not billable item cannot have effective date");
			setSelectedBillItem(null);
		}
		setEffectiveDate(value);
	};

	const handleVerifyLinkedEntryId = async () => {
		setLoadingLinkedEntryIdVerify(true);
		const result = await dashboardServices.linkedEntryIdVerify(
			selectedValues?.objectId,
			linkedEntryId
		);
		setIsLinkedEntryIdVerified(result.data);
		setLoadingLinkedEntryIdVerify(false);
	};

	const handleDocumentId = (value: any) => {
		setDocumentId(value)
	}

	const handleVerifyDocumentId = async () => {
		if(!documentId) {
			setDocumentIdError(true)
		} else {
			const result: any = await dashboardServices.getFilesBySourceId(documentId, 'document');
  			if (result.status === 200 && result.data.length > 0) {
    			setIsDocumentIdVerified(true)
  			} else {
    			setIsDocumentIdVerified(false);
  		}
		}
	}

	return (
		<div>
			<Dialog open={isBillableItemsModal} onClose={handleClose}>
				{verificationLoading ? <LinearProgress style={{ height: "2px" }} /> : null}
				<DialogTitle style={{ marginTop: "14px" }}>Select Billable Item</DialogTitle>
				<DialogContent className="billable-items-container">
					<FormControl disabled={isItemVerifiedFromPropertyTask()}>
						<RadioGroup
							aria-labelledby="demo-radio-buttons-group-label"
							defaultValue="female"
							name="radio-buttons-group"
							value={selectedBillItem}
							onChange={handleChange}
						>
							{renderBillItems()}
						</RadioGroup>
						<br></br>
							{tagType === "Hoa" &&
							(selectedBillItem === "Trigger due to property addition" ||
								selectedBillItem === "Trigger due to periodic update") && (
								<>
									<Stack
										display={"flex"}
										justifyContent={"space-between"}
										alignItems={"baseline"}
										flexDirection={"row"}
										marginBottom={"18px"}
									>
										<TextField
											key={selectedBillItem}
											style={{ width: "250px" }}
											autoFocus
											margin="dense"
											id="documentId"
											label="Document ID"
											type="documentId"
											fullWidth
											variant="standard"
											disabled={isItemVerifiedFromPropertyTask()}
											value={documentId}
											error={!documentId && documentIdError}
											onChange={(e) => handleDocumentId(e.target.value)}
										/>
										<Button
											id="hoa-cancel-click"
											variant="outlined"
											color="primary"
											style={{
												height: "40px",
												padding: "5px 25px",
												width: "100px",
											}}
											onClick={handleVerifyDocumentId}
										>
											Verify
										</Button>
									</Stack>
									{documentId && isDocumentIdVerified && (
										<span
											style={{
												display: "flex",
												alignItems: "center",
												gap: "5px",
												color: "#5f9ea0",
											}}
										>
											<CheckCircleSharpIcon /> Verified
										</span>
									)}
									{documentId && isDocumentIdVerified == false && (
										<span
											style={{
												display: "flex",
												alignItems: "center",
												gap: "5px",
												color: "red",
											}}
										>
											<CancelSharpIcon />
											Not Verified
										</span>
									)}
								</>
							)}
						{(tagType === "Hoa" || tagType === "Property") &&
							(selectedBillItem === "Update from mail" ||
								selectedBillItem === "Update from email") && (
								<>
									<Stack
										display={"flex"}
										justifyContent={"space-between"}
										alignItems={"baseline"}
										flexDirection={"row"}
										marginBottom={"18px"}
									>
										<TextField
											style={{ width: "250px" }}
											autoFocus
											margin="dense"
											id="sourceId"
											label="Source ID"
											type="sourceId"
											fullWidth
											variant="standard"
											disabled={isItemVerifiedFromPropertyTask()}
											value={sourceId}
											error={!sourceId && sourceIdError}
											onChange={(e) => handleSourceId(e.target.value)}
										/>
										<Button
											id="hoa-cancel-click"
											variant="outlined"
											color="primary"
											disabled={isItemVerifiedFromPropertyTask()}
											style={{
												height: "40px",
												padding: "5px 25px",
												width: "100px",
											}}
											onClick={handleVerifySourceId}
										>
											Verify
										</Button>
									</Stack>
									{sourceId && isVerified && (
										<span
											style={{
												display: "flex",
												alignItems: "center",
												gap: "5px",
												color: "#5f9ea0",
											}}
										>
											<CheckCircleSharpIcon /> Verified
										</span>
									)}
									{sourceId && isVerified == null && (
										<span
											style={{
												display: "flex",
												alignItems: "center",
												gap: "5px",
												color: "red",
											}}
										>
											<CancelSharpIcon />
											Not Verified
										</span>
									)}
								</>
							)}
						{tagType === "Hoa" && (
							<>
								<FormControl
									variant="standard"
									style={{ width: "380px", marginBottom: "20px" }}
								>
									<InputLabel id="demo-simple-select-standard-label">
										Document Type
									</InputLabel>
									<Select
										labelId="demo-simple-select-standard-label"
										id="demo-simple-select-standard"
										value={documentType}
										onChange={(e) => setDocumentType(e.target.value)}
										label="Document Type"
									>
										{config.billableItemModalDocumentType.map((item: any) => {
											return <MenuItem value={item}>{item}</MenuItem>;
										})}
									</Select>
								</FormControl>
								{!isEditMode && datastore.changedKeys.length <= 0 && (
									<CustomDateField
										label="Effective Date"
										error={false}
										variant="standard"
										disablePast={true}
										style={{
											marginTop: "17px",
											width: "380px",
											padding: "10px 0px",
										}}
										value={dayjs(effectiveDate || null)}
										handleChange={(value: any) => {
											onEffectiveDateChange(value);
										}}
									/>
								)}
							</>
						)}
						<TextField
							style={{ width: "380px" }}
							autoFocus
							margin="dense"
							id="evidenceLink"
							label="Remarks"
							type="evidenceLink"
							fullWidth
							variant="standard"
							value={evidenceLink}
							onChange={(e) => setEvidenceLink(e.target.value)}
						/>
						<TextField
							style={{ width: "380px" }}
							margin="dense"
							id="linkedEntryId"
							label="Linked Entry ID"
							type="linkedEntryId"
							fullWidth
							variant="standard"
							value={linkedEntryId}
							onChange={(e) => setLinkedEntryId(e.target.value)}
						/>
					</FormControl>
					<>
						<Stack
							display="flex"
							justifyContent="space-between"
							alignItems="center"
							flexDirection="row"
							marginBottom="18px"
							width="100%"
							paddingTop="8px"
						>
							<div>
								{isLinkedEntryIdVerified && (
									<span
										style={{
											display: "flex",
											alignItems: "center",
											gap: "5px",
											color: "#5f9ea0",
										}}
									>
										<CheckCircleSharpIcon /> Verified
									</span>
								)}
								{isLinkedEntryIdVerified == false && linkedEntryId !== null && (
									<span
										style={{
											display: "flex",
											alignItems: "center",
											gap: "5px",
											color: "red",
										}}
									>
										<CancelSharpIcon />
										Not Verified
									</span>
								)}
							</div>
							<Button
								id="hoa-cancel-click"
								variant="outlined"
								color="primary"
								style={{ height: "30px", padding: "5px 25px", width: "100px" }}
								onClick={handleVerifyLinkedEntryId}
								disabled={loadingLinkedEntryIdVerify}
							>
								{loadingLinkedEntryIdVerify ? (
									<CircularProgress size={20} />
								) : (
									"Verify"
								)}
							</Button>
						</Stack>
					</>
				</DialogContent>
				<DialogActions
					style={{
						display: "flex",
						flexDirection: "row",
						justifyContent: "center",
						marginBottom: "20px",
						paddingLeft: "25px",
						paddingRight: "25px",
					}}
				>
					<Button
						id="hoa-cancel-click"
						variant="outlined"
						color="primary"
						style={{
							height: "40px",
							padding: "5px 25px",
							marginRight: "5px",
							width: "184px",
						}}
						onClick={handleClose}
					>
						Cancel
					</Button>
					<Button
						disabled={
							!selectedBillItem ||
							(typeof linkedEntryId === "string" &&
								linkedEntryId !== "" &&
								!isLinkedEntryIdVerified) || (documentId !== "" && !isDocumentIdVerified)
						}
						id="hoa-save-click"
						variant="contained"
						color="primary"
						style={{ height: "40px", padding: "5px 25px", width: "184px" }}
						onClick={saveBtnClicked}
					>
						Save
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
}
